import { useRef, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Avatar,
  Button,
  Card,
  Col,
  Input,
  Layout,
  Modal,
  message,
  Popover,
  Row,
  Tabs,
  Tooltip,
  Typography,
} from "antd";
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  PlusOutlined,
  CalculatorOutlined,
  LoadingOutlined,
} from "@ant-design/icons";

import EntitiesBrowser from "../../components/EntitiesBrowser";
import BusinessCaseDropdown from "../../components/BusinessCaseDropDown";
import ScenarioTable from "../../components/ScenarioTable";
import RenderUpdater from "../../components/RenderUpdater";
import OverlayedLoader from "../../components/OverlayedLoader";
import ExportBusinessCaseButton from "../../components/ExportBusinessCaseButton";
import Localization from "../../utils/localization";

import {
  accountActions,
  allocationActions,
  businessActions,
  entityActions,
  organizationActions,
  periodsActions,
  currencyActions,
} from "../../redux/actions";

import {
  assembleBaseline,
  assemblePreview,
} from "../../redux/actions/assemblyActions";

import {
  selectShowPreview,
  selectActiveResult,
  setShowPreview,
  selectAssemblyCompiling,
   setCurrent, 
} from "../../redux/slices/assemblySlice";

import { toMap, asScenarioWithMaps } from "../../utils/scenarioTransforms";

import { useColumnWidths } from '../../hooks/useColumnWidths';

import "./styles.scss";

import { buildCalculationAccountStructure } from '../../utils/accountStructure/accountStructureUtils';
import { customTabKey, getCustomTabs } from '../../utils/accountStructure/customTabs';

const { Content } = Layout;
const { Meta } = Card;
const { TabPane } = Tabs;
const { Title } = Typography;

const NUM_DEFAULT_TABS = 4;
const DEFAULT_WIDTHS = { account_number: 90, name: 150 };

const BusinessCases = () => {
  const [columnWidths, setColumnWidth] = useColumnWidths(
    'scenarioTable.columnWidths',  // ← same key on all three
    DEFAULT_WIDTHS
  );
  const RUN_MSG_KEY = "bc-run-required";
  const dispatch = useDispatch();

  const {
    entities,
    fetchingEntities,

    accountStructure,
    fetchingStructure,

    periods,
    expandedPeriods,
    fetchingExpandedPeriods,

    businessCases,
    creatingBusinessCase,
    updatingBusinessCase,
    selectedBusinessCaseId,

    accountsNotExisting,
    accountsCircular,

    allocations,
    updatingOrganization,
    fetchingOrganization,

    organization,
    currencies,
  } = useSelector((state) => ({
    entities: state.entities.entities,
    fetchingEntities: state.entities.fetchingEntities,

    accountStructure: state.accounts.accountStructure,
    fetchingStructure: state.accounts.fetchingStructure,

    periods: state.periods.periods,
    expandedPeriods: state.periods.expandedPeriods,
    fetchingExpandedPeriods: state.periods.fetchingExpandedPeriods,

    businessCases: state.business.businessCases,
    creatingBusinessCase: state.business.creatingBusinessCase,
    updatingBusinessCase: state.business.updatingBusinessCase,
    selectedBusinessCaseId: state.business.selectedBusinessCaseId,

    accountsNotExisting: state.scenarios.accountsNotExisting,
    accountsCircular: state.scenarios.accountsCircular,

    allocations: state.allocations.allocations,

    organization: state.organization.organization,
    updatingOrganization: state.organization.updatingOrganization,
    fetchingOrganization: state.organization.fetchingOrganization,

    currencies: state.currency.currency,
  }));

  const calculationAccountStructure = useMemo(
    () =>
      buildCalculationAccountStructure(
        accountStructure,
      ),
    [accountStructure],
  );

  const assembledBusinessCase = useSelector(selectActiveResult);
  const assemblingBusinessCase = useSelector(selectAssemblyCompiling);
  const showPreview = useSelector(selectShowPreview);

  const bcWarmStatuses = useSelector(state => state.business.bcWarmStatuses ?? {});
  const selectedBcWarmStatus = useSelector(
    state => state.business.bcWarmStatuses?.[selectedBusinessCaseId] ?? 'cold'
);
  
  const allScenarios = useSelector((state) => state.scenarios.scenarios);

  const [modalOpen, setModalOpen] = useState(false);
  const [checkedNodes, setCheckedNodes] = useState([]);
  const [editingKey, setEditingKey] = useState(null);
  const [newBusinessCaseName, setNewBusinessCaseName] = useState("");
  const [expandedBusinessCases, setExpandedBusinessCases] = useState([]);

  const [currentBusinessCaseName, setCurrentBusinessCaseName] = useState(
    "Select business case"
  );
  const [selectedBusinessCase, setSelectedBusinessCase] = useState(null);

  const bsAssetsRef = useRef(null);
  const bsLiabRef = useRef(null);
  const bsEquityRef = useRef(null);

  const [activeKey, setActiveKey] = useState("1");

  const scenarioForTable = useMemo(() => {
    const sc = assembledBusinessCase?.scenario;
    if (!sc) return null;

    return asScenarioWithMaps({
      ...sc,
      values: toMap(sc.values),
      originalValues: toMap(sc.originalValues ?? sc.values),
      roll_up: toMap(sc.roll_up),
    });
  }, [assembledBusinessCase]);

// Top-level memo: walks entities once, builds the lookup map.
const scenarioMetaFromEntities = useMemo(() => {
  // scenario_id -> { name, entityName }
  const m = new Map();
  const walk = (nodes, currentEntityName) => {
    (nodes || []).forEach((n) => {
      const nextEntityName =
        n?.entity_id != null ? (n.name || currentEntityName) : currentEntityName;

      if (n?.scenario_id != null) {
        m.set(Number(n.scenario_id), {
          name: n.name,
          entityName: currentEntityName,
        });
      }
      if (Array.isArray(n.scenario)) walk(n.scenario, nextEntityName);
      if (Array.isArray(n.children)) walk(n.children, nextEntityName);
    });
  };
  walk(Array.isArray(entities) ? entities : Object.values(entities || {}), null);
  return m;
}, [entities]);

// 2. Top-level memo: builds the audit-trail prop, consumes #1 as a dep.
const businessCaseForAudit = useMemo(() => {
  if (!selectedBusinessCase || !assembledBusinessCase) return null;

  const sourceSc = assembledBusinessCase.scenario;
  if (!sourceSc) return null;

  const parsed = assembledBusinessCase.parsedScenarios;
  if (!Array.isArray(parsed) || parsed.length === 0) return null;

  const scenarios = parsed
    .filter((s) => s?.scenario_id != null)
    .map((s) => {
      const meta = scenarioMetaFromEntities.get(Number(s.scenario_id));
      return {
        ...s,
        values: toMap(s.values),
        name: s.name || meta?.name || `Scenario ${s.scenario_id}`,
        entityName: meta?.entityName ?? null,
      };
    });

  return {
    source: { ...sourceSc, values: toMap(sourceSc.values) },
    scenarios,
  };
}, [selectedBusinessCase, assembledBusinessCase, scenarioMetaFromEntities]);



  const fmtCurrency = (id, list = currencies) => {
  if (id == null) return "—";
  const items = Array.isArray(list) ? list : [];

  const cur = items.find(
    (c) => Number(c.id ?? c.currency_id) === Number(id)
  );

  if (!cur) return String(id);

  const code = cur.code ?? cur.currency ?? "";
  const symbol = cur.symbol ?? "";
  return symbol ? `${code} (${symbol})` : (code || String(id));
};

const groupCurrencyId =
  organization?.base_currency ??
  null;

const groupCurrencyLabel = fmtCurrency(groupCurrencyId);



  useEffect(() => {
    if (activeKey !== "2") return;

    let handlers = [];
    let raf1 = 0;
    let raf2 = 0;

    const getScroller = (root) =>
      root?.querySelector(":scope .ant-table-content") || null;

    const bind = () => {
      const assets = getScroller(bsAssetsRef.current);
      const liab = getScroller(bsLiabRef.current);
      const eq = getScroller(bsEquityRef.current);

      const els = [assets, liab, eq].filter(Boolean);
      if (els.length < 2) return;

      let isSyncing = false;

      const onScroll = (sourceEl) => {
        if (isSyncing) return;
        isSyncing = true;

        const left = sourceEl.scrollLeft;
        els.forEach((el) => {
          if (el !== sourceEl) el.scrollLeft = left;
        });

        requestAnimationFrame(() => {
          isSyncing = false;
        });
      };

      handlers = els.map((el) => {
        const h = () => onScroll(el);
        el.addEventListener("scroll", h, { passive: true });
        return { el, h };
      });
    };

    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(bind);
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      handlers.forEach(({ el, h }) => el.removeEventListener("scroll", h));
      handlers = [];
    };
  }, [activeKey, periods, expandedPeriods, scenarioForTable]);

  useEffect(() => {
    dispatch(entityActions.getEntities());
    dispatch(accountActions.getAccountStructure());
    dispatch(organizationActions.getOrganization());
    dispatch(businessActions.getBusinessCases());
    dispatch(periodsActions.getPeriods());
    dispatch(allocationActions.getAllocations());
    dispatch(currencyActions.getCurrency());
  }, [dispatch]);

  useEffect(() => {
    if (periods != null && expandedPeriods == null && !fetchingExpandedPeriods) {
      dispatch(periodsActions.getExpandedPeriods());
    }
  }, [dispatch, periods, expandedPeriods, fetchingExpandedPeriods]);

  useEffect(() => {
    if (!Array.isArray(businessCases)) return;

    if (selectedBusinessCaseId == null) {
      setSelectedBusinessCase(null);
      setCurrentBusinessCaseName("Select business case");
      return;
    }

    const selectedId = Number(selectedBusinessCaseId);
    const bc = businessCases.find(
      (b) => Number(b.business_case_id) === selectedId
    );
    if (!bc) return;

    setSelectedBusinessCase(bc);
    setCurrentBusinessCaseName(bc.name);
  }, [businessCases, selectedBusinessCaseId]);

  const bcAllocations = useMemo(() => {
    if (!selectedBusinessCase || !Array.isArray(allocations)) return [];
    const id = Number(selectedBusinessCase.business_case_id);

    return allocations.filter(
      (a) => Number(a?.businessCaseId ?? a?.business_case_id) === id
    );
  }, [allocations, selectedBusinessCase]);

  const bcAllocationsSignature = useMemo(
  () =>
    bcAllocations
      .map((a) =>
        [
          a.allocation_id,
          a.entityId,
          a.accountId,
          (a.scenarioId ?? []).join('.'),
          a.targetEntityId,
          a.targetAccountId,
          (a.targetScenarioId ?? []).join('.'),
        ].join(':')
      )
      .sort()
      .join('|'),
  [bcAllocations]
);

  const hasAllocForSelected =
    !!selectedBusinessCase && bcAllocations.length > 0;

  const disabledPreviewToggle =
    !selectedBusinessCase || assemblingBusinessCase;

  useEffect(() => {
  if (!selectedBusinessCase) return;
  if (!periods || !calculationAccountStructure || !expandedPeriods || !entities) return;
  if (assemblingBusinessCase) return;

   const shouldUsePreview = hasAllocForSelected;

  const autoAssemble = async () => {
    try {
      dispatch(setShowPreview(shouldUsePreview));

      if (shouldUsePreview) {
        await dispatch(
          assemblePreview({
            bc: selectedBusinessCase,
            periods,
             accountStructure: calculationAccountStructure,
            expandedPeriods,
            allocations: bcAllocations,
            entities,
            force: false,
          })
        );
      } else {
        await dispatch(
          assembleBaseline({
            bc: selectedBusinessCase,
            periods,
            accountStructure: calculationAccountStructure,
            expandedPeriods,
            allocations: [],
            entities,
            force: false,
          })
        );
      }

      message.destroy(RUN_MSG_KEY);
    } catch (err) {
      console.error("Auto-assemble on page return failed", err);
    }
  };

  autoAssemble();
}, [
  dispatch,
  selectedBusinessCase?.business_case_id,
  periods,
  calculationAccountStructure,
  expandedPeriods,
  entities,
  bcAllocationsSignature,
]);

  const runAssembly = async ({ bc, preview, force = false }) => {
    if (!bc || !periods || !calculationAccountStructure  || assemblingBusinessCase) {
      return null;
    }

    const bcId = Number(bc.business_case_id);
    const allocsForBc = Array.isArray(allocations)
      ? allocations.filter(
          (a) => Number(a?.businessCaseId ?? a?.business_case_id) === bcId
        )
      : [];

    const thunk = preview
      ? assemblePreview({
          bc,
          periods,
          accountStructure: calculationAccountStructure,
          expandedPeriods,
          allocations: allocsForBc,
          entities,
          force,
        })
      : assembleBaseline({
          bc,
          periods,
          accountStructure: calculationAccountStructure,
          expandedPeriods,
          allocations: [],
          entities,
          force,
        });

    return dispatch(thunk);
  };

  const handleCheck = (nodes) => {
    if (!nodes) return;
    setCheckedNodes(
      nodes.map((node) =>
        Number.isInteger(node) ? node : node.scenario_id
      )
    );
  };

  const baseAssemblyArgs = useMemo(
    () => ({
      bc: selectedBusinessCase,
      periods,
      accountStructure: calculationAccountStructure,
      expandedPeriods,
      entities,
    }),
    [selectedBusinessCase, periods, calculationAccountStructure, expandedPeriods, entities]
  );

  const handleSelectBusinessCase = (bcOrValue) => {
    if (!bcOrValue) {
      dispatch(businessActions.clearSelectedBusinessCaseId());
      dispatch(setShowPreview(false));
      dispatch(setCurrent(null));
      setSelectedBusinessCase(null);
      setCurrentBusinessCaseName("Select business case");
      message.destroy(RUN_MSG_KEY);
      return;
    }

    const idRaw =
      typeof bcOrValue === "object" ? bcOrValue.business_case_id : bcOrValue;
    const id = Number(idRaw);
    if (!id) return;

   // check warm status now that id is resolved
    const warmStatus = bcWarmStatuses[id];
    if (warmStatus === 'warming') {
        message.warning({
            content: 'This business case is currently being updated. Results may be incomplete.',
            key: 'bc-warming',
            duration: 5,
        });
    }

    const bc =
      typeof bcOrValue === "object"
        ? bcOrValue
        : businessCases?.find((b) => Number(b.business_case_id) === id);

    if (!bc) return;

    const allocsForBc = Array.isArray(allocations)
      ? allocations.filter(
          (a) => Number(a?.businessCaseId ?? a?.business_case_id) === id
        )
      : [];

    const shouldUsePreview = allocsForBc.length > 0;

    setSelectedBusinessCase(bc);
    setCurrentBusinessCaseName(bc.name);
    dispatch(businessActions.setSelectedBusinessCaseId(id));
    dispatch(setShowPreview(shouldUsePreview));
  };
  
  const handleReassembleBC = async () => {
    message.destroy(RUN_MSG_KEY);
    if (!selectedBusinessCase) return;

    const args = { ...baseAssemblyArgs, force: true };

    try {
      if (showPreview && hasAllocForSelected) {
        await dispatch(
          assemblePreview({
            ...args,
            allocations: bcAllocations,
          })
        );
      } else {
        await dispatch(
          assembleBaseline({
            ...args,
            allocations: [],
          })
        );
      }
    } catch (err) {
      console.error("handleReassembleBC failed", err);
      console.error(
        "handleReassembleBC failed response",
        err?.response?.data
      );
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to recalculate business case"
      );
    }
  };

  const handlePreviewToggle = async () => {
    if (!selectedBusinessCase || assemblingBusinessCase) return;

    try {
      if (showPreview) {
        dispatch(setShowPreview(false));
        await dispatch(
          assembleBaseline({
            bc: selectedBusinessCase,
            periods,
            accountStructure: calculationAccountStructure,
            expandedPeriods,
            allocations: [],
            entities,
            force: false,
          })
        );
      } else {
        if (!hasAllocForSelected) return;

        dispatch(setShowPreview(true));
        await dispatch(
          assemblePreview({
            bc: selectedBusinessCase,
            periods,
            accountStructure: calculationAccountStructure,
            expandedPeriods,
            allocations: bcAllocations,
            entities,
            force: false,
          })
        );
      }
    } catch (err) {
      console.error("handlePreviewToggle failed", err);
      console.error(
        "handlePreviewToggle failed response",
        err?.response?.data
      );
      message.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to switch allocation view"
      );
    }
  };

  const initialCheckedRef = useRef([]);
  const initialNameRef = useRef("");

  const modalDirty = useMemo(() => {
    const a = [...(initialCheckedRef.current || [])]
      .sort((x, y) => x - y)
      .join(",");
    const b = [...(checkedNodes || [])]
      .map(Number)
      .sort((x, y) => x - y)
      .join(",");
    const nameChanged =
      editingKey != null &&
      (newBusinessCaseName || "").trim() !==
        (initialNameRef.current ?? "").trim();

    return a !== b || nameChanged;
  }, [checkedNodes, newBusinessCaseName, editingKey]);

  const openEditModal = (businessCase) => {
    setEditingKey(businessCase.business_case_id);
    const initial = (businessCase?.business_scenario || []).map(
      (s) => s.scenario_id
    );

    initialCheckedRef.current = initial;
    initialNameRef.current = businessCase?.name || "";

    setCheckedNodes(initial);
    setNewBusinessCaseName(businessCase?.name || "");
    setModalOpen(true);
  };

  const openCreateModal = () => {
    setEditingKey(null);
    initialCheckedRef.current = [];
    initialNameRef.current = "";
    setCheckedNodes([]);
    setNewBusinessCaseName("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (!modalDirty) {
      setModalOpen(false);
      setEditingKey(null);
      return;
    }

    Modal.confirm({
      title: "Discard changes?",
      content: "You have unsaved changes. Are you sure you want to close?",
      okText: "Discard",
      cancelText: "Keep editing",
      onOk: () => {
        setModalOpen(false);
        setEditingKey(null);
        setCheckedNodes(initialCheckedRef.current || []);
        setNewBusinessCaseName(initialNameRef.current || "");
      },
    });
  };

  const handleOk = async () => {
    try {
      if (editingKey !== null) {
        if (updatingBusinessCase) return;

        const bc = businessCases?.find(
          (b) => b.business_case_id === editingKey
        );
        const nameToUse =
          newBusinessCaseName === "" ? bc?.name : newBusinessCaseName;

        const result = await dispatch(
          businessActions.updateBusinessCase(
            editingKey,
            nameToUse,
            checkedNodes,
            allocations
          )
        );

        if (result) {
          message.info({
            content: "Business case updated. Recalculating...",
            key: RUN_MSG_KEY,
            duration: 2,
          });

          const refreshedBc = result?.business_case_id
            ? result
            : {
                ...bc,
                name: nameToUse,
                business_scenario: checkedNodes.map((scenario_id) => ({
                  scenario_id,
                })),
              };

          const refreshedBcId = Number(refreshedBc.business_case_id);
          const refreshedBcAllocations = Array.isArray(allocations)
            ? allocations.filter(
                (a) =>
                  Number(a?.businessCaseId ?? a?.business_case_id) ===
                  refreshedBcId
              )
            : [];

          const shouldUsePreview = refreshedBcAllocations.length > 0;

          dispatch(setShowPreview(shouldUsePreview));

          await runAssembly({
            bc: refreshedBc,
            preview: shouldUsePreview,
            force: true,
          });
        } else {
          message.error("Business case update failed.");
        }
        return;
      }

      if (creatingBusinessCase) return;

      const newBc = await dispatch(
        businessActions.createBusinessCase(newBusinessCaseName, checkedNodes)
      );

      if (newBc?.business_case_id) {
        setCurrentBusinessCaseName(newBc.name);
        dispatch(
          businessActions.setSelectedBusinessCaseId(newBc.business_case_id)
        );

        const newBcId = Number(newBc.business_case_id);
        const newBcAllocations = Array.isArray(allocations)
          ? allocations.filter(
              (a) =>
                Number(a?.businessCaseId ?? a?.business_case_id) === newBcId
            )
          : [];

        const shouldUsePreview = newBcAllocations.length > 0;

        dispatch(setShowPreview(shouldUsePreview));

        await runAssembly({
          bc: newBc,
          preview: shouldUsePreview,
          force: false,
        });

        message.success("Business case created.");
      } else {
        message.error("Business case create failed.");
      }
    } catch (e) {
      message.error("Operation failed.");
    } finally {
      setModalOpen(false);
      setEditingKey(null);
      setCheckedNodes([]);
      setNewBusinessCaseName("");
    }
  };

  const createCustomTabs = () => {
    if (!periods || periods.length === 0) return null;
    if (!accountStructure?.custom) return null;

     return getCustomTabs(accountStructure).map((tab) => (
        <TabPane tab={tab.name} key={customTabKey(tab)}>
            <Title
                level={2}
                style={{
                    textAlign: "left",
                    marginTop: 10,
                    marginBottom: 30,
                    textShadow: "0.5px 0.5px 0.5px  #aab7b8",
                }}
            >
                {tab.name}
            </Title>

            <ScenarioTable
                accounts={tab.custom_tab_accounts}
                structure={accountStructure}
                scenario={scenarioForTable}
                periods={periods}
                expandedPeriods={expandedPeriods}
                columnWidths={columnWidths}
                setColumnWidth={setColumnWidth}
                columnDefaults={DEFAULT_WIDTHS}
                businessCase={businessCaseForAudit}
            />
        </TabPane>
    ));
};

  const renderHeader = () => (
    <Row align="middle" style={{ marginTop: 15 }}>
      <Col flex="auto">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Popover
            placement="bottomRight"
            title="Calculation error(s)"
            open={
              accountsNotExisting.length > 0 || accountsCircular.length > 0
            }
            content={() => {
              const hasMissing = accountsNotExisting.length > 0;
              return hasMissing
                ? `Account(s) ${accountsNotExisting.join(
                    ", "
                  )} does not exist - remove the reference(s) to calculate business case successfully.`
                : `Accounts ${accountsCircular.join(
                    ", "
                  )} are part of a circular reference - please break it to calculate business case successfully.`;
            }}
          >
            <BusinessCaseDropdown
              handleStartEdit={openEditModal}
              editingKey={editingKey}
              handleSelectBusinessCase={handleSelectBusinessCase}
              showEdit
              showDelete
              currentBusinessCaseName={currentBusinessCaseName}
              selectedBusinessCaseId={selectedBusinessCaseId ?? null}
              allowNone
              noneLabel="None (no business case)"
            />
          </Popover>

          {selectedBcWarmStatus === 'warming' && (
              <Tooltip title="Business case is being updated in the background">
                  <span style={{ 
                      fontSize: 12, 
                      color: '#fa8c16',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 4,
                  }}>
                      <LoadingOutlined spin />
                      Updating...
                  </span>
              </Tooltip>
          )}
       
          {!selectedBusinessCase ? (
            <Tooltip title="Select a business case first">
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Button
                  shape="circle"
                  disabled
                  aria-pressed={false}
                  icon={<EyeInvisibleOutlined />}
                />
                <span style={{ fontSize: 12, color: "#666", minWidth: 140 }}>
                  No business case selected
                </span>
              </span>
            </Tooltip>
          ) : showPreview && !hasAllocForSelected ? (
            <Tooltip title="No allocations available to include">
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Button
                  shape="circle"
                  disabled
                  loading={assemblingBusinessCase}
                  aria-pressed={false}
                  icon={<EyeInvisibleOutlined />}
                />
                <span style={{ fontSize: 12, color: "#666", minWidth: 140 }}>
                  No allocations available
                </span>
              </span>
            </Tooltip>
          ) : (
            <Tooltip
              title={
                showPreview
                  ? "Business case including allocations"
                  : "Business case excluding allocations"
              }
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Button
                  shape="circle"
                  onClick={handlePreviewToggle}
                  disabled={disabledPreviewToggle}
                  loading={assemblingBusinessCase}
                  aria-pressed={showPreview}
                  icon={
                    showPreview ? <EyeOutlined /> : <EyeInvisibleOutlined />
                  }
                />
                <span style={{ fontSize: 12, color: "#666", minWidth: 140 }}>
                  {showPreview ? "Including allocations" : "Excluding allocations"}
                </span>
              </span>
            </Tooltip>
          )}

          <Tooltip title="Force recalculation">
            <span>
              <Button
                shape="circle"
                icon={<CalculatorOutlined />}
                onClick={handleReassembleBC}
                disabled={!selectedBusinessCase || assemblingBusinessCase}
                loading={assemblingBusinessCase}
              />
            </span>
          </Tooltip>

          <ExportBusinessCaseButton
            businessCase={selectedBusinessCase}
            disabled={assemblingBusinessCase}
          />
        </div>
      </Col>

      <Col>
        <Row justify="center">
          <Card title={null} bordered={false} onClick={openCreateModal}>
            <Meta
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: 300,
              }}
              avatar={<Avatar icon={<PlusOutlined />} />}
              description={Localization.businessCase.create}
            />
          </Card>
        </Row>
      </Col>
    </Row>
  );

  const renderTabs = () => (
    <Tabs onChange={setActiveKey} activeKey={activeKey} type="card" destroyInactiveTabPane style={{ marginTop: 40 }}>
      <TabPane tab={Localization.common.profitAndLoss} key="1">
        <ScenarioTable
          accounts={accountStructure?.profitloss}
          structure={accountStructure}
          scenario={scenarioForTable}
          periods={periods}
          expandedPeriods={expandedPeriods}
          columnWidths={columnWidths}
          setColumnWidth={setColumnWidth}
          columnDefaults={DEFAULT_WIDTHS}
          businessCase={businessCaseForAudit}
        />
      </TabPane>

      <TabPane tab={Localization.common.balanceSheet} key="2">
        <Title level={4} style={{ textAlign: "left" }}>
          {Localization.common.assets}
        </Title>
        <div ref={bsAssetsRef}>
          <ScenarioTable
            accounts={accountStructure?.balancesheet?.assets}
            structure={accountStructure}
            scenario={scenarioForTable}
            periods={periods}
            expandedPeriods={expandedPeriods}
            columnWidths={columnWidths}
            setColumnWidth={setColumnWidth}
            columnDefaults={DEFAULT_WIDTHS}
            businessCase={businessCaseForAudit}
          />
        </div>

        <Title level={4} style={{ textAlign: "left", marginTop: 40 }}>
          {Localization.common.liabilities}
        </Title>
        <div ref={bsLiabRef}>
          <ScenarioTable
            accounts={accountStructure?.balancesheet?.liabilities}
            structure={accountStructure}
            scenario={scenarioForTable}
            periods={periods}
            expandedPeriods={expandedPeriods}
            columnWidths={columnWidths}
            setColumnWidth={setColumnWidth}
            columnDefaults={DEFAULT_WIDTHS}
            businessCase={businessCaseForAudit}
          />
        </div>

        <Title level={4} style={{ textAlign: "left", marginTop: 40 }}>
          {Localization.common.equities}
        </Title>
        <div ref={bsEquityRef}>
          <ScenarioTable
            accounts={accountStructure?.balancesheet?.equities}
            structure={accountStructure}
            scenario={scenarioForTable}
            periods={periods}
            expandedPeriods={expandedPeriods}
            columnWidths={columnWidths}
            setColumnWidth={setColumnWidth}
            columnDefaults={DEFAULT_WIDTHS}
            businessCase={businessCaseForAudit}
          />
        </div>
      </TabPane>

      <TabPane tab={Localization.common.cashFlow} key="3">
        <ScenarioTable
          accounts={accountStructure?.cashflow}
          structure={accountStructure}
          scenario={scenarioForTable}
          periods={periods}
          expandedPeriods={expandedPeriods}
          columnWidths={columnWidths}
          setColumnWidth={setColumnWidth}
          columnDefaults={DEFAULT_WIDTHS}
          businessCase={businessCaseForAudit}
        />
      </TabPane>

      <TabPane tab={Localization.common.drivers} key="4">
        <ScenarioTable
          accounts={accountStructure?.drivers}
          structure={accountStructure}
          scenario={scenarioForTable}
          periods={periods}
          expandedPeriods={expandedPeriods}
          columnWidths={columnWidths}
          setColumnWidth={setColumnWidth}
          columnDefaults={DEFAULT_WIDTHS}
          businessCase={businessCaseForAudit}
        />
      </TabPane>

      {createCustomTabs()}
    </Tabs>
  );

  const renderContent = () => {
    if (
      fetchingEntities ||
      fetchingStructure ||
      fetchingOrganization ||
      !organization ||
      !accountStructure ||
      !periods
    ) {
      return <OverlayedLoader />;
    }

    if (businessCases == null) return <OverlayedLoader />;
    if (updatingOrganization) return <RenderUpdater />;

    return (
      <div
        className="site-card-wrapper"
        style={{
          background: "#f1f1f1",
          width: "100%",
          height: "100%",
          padding: "1rem",
        }}
      >
        {renderHeader()}

         {selectedBusinessCase && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginTop: 10,
              marginLeft: 4,
            }}
          >
            <div style={{ fontSize: 12, color: "#666" }}>
              Group currency
            </div>
            <Button disabled style={{ minWidth: 140 }}>
              {groupCurrencyLabel}
            </Button>
          </div>
        )}

        {(assemblingBusinessCase || updatingBusinessCase) && (
          <OverlayedLoader style={{ marginTop: 100 }} />
        )}

        {!assemblingBusinessCase && scenarioForTable ? (
          renderTabs()
        ) : selectedBusinessCaseId ? (
          <div style={{ marginTop: 40, opacity: 0.75 }}>
            Loading business case...
          </div>
        ) : null}
      </div>
    );
  };

  const renderModal = () => (
    <Modal
      bodyStyle={{ minHeight: "60vh", display: "flex", flexDirection: "column" }}
      title={
        editingKey == null
          ? Localization.businessCase.create
          : Localization.businessCase.update
      }
      open={modalOpen}
      onOk={handleOk}
      okButtonProps={editingKey != null ? { disabled: !modalDirty } : undefined}
      confirmLoading={false}
      onCancel={closeModal}
      destroyOnClose
    >
      <Input
        style={{ margin: "2rem 0", maxWidth: 275, alignSelf: "center" }}
        placeholder={Localization.businessCase.newName}
        value={newBusinessCaseName}
        onChange={(e) => setNewBusinessCaseName(e.target.value)}
      />

      {entities ? (
        <EntitiesBrowser
          entityStructure={entities}
          handleCheck={handleCheck}
          expandedBusinessCases={expandedBusinessCases}
          setExpandedBusinessCases={setExpandedBusinessCases}
          editable={false}
          checkedNodes={checkedNodes}
        />
      ) : (
        "No scenarios found."
      )}
    </Modal>
  );

  return (
    <Layout className="business-cases-page" style={{ height: "100vh", marginLeft: -16, position: "relative" }}>
      {renderModal()}
      <Content style={{ width: "100%", position: "relative", overflow: "auto" }}>
        {renderContent()}
      </Content>
    </Layout>
  );
};

export default BusinessCases;